import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import UserDetailsContext from '../Dashboard/UserDetailsContext';
import PageHeader from '../PageHeader/PageHeader';
import { useExpenseStore } from '../store/useExpenseStore';
import UseFeatureFlags from '../components/UseFeatureFlags';

export default function ManageCategories() {
  const [comment, setComment] = useState('');
  const [categories, setCategories] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);

  // ✅ Snackbar state
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const { userDetails } = useContext(UserDetailsContext);
  const { hasFeature } = UseFeatureFlags();
  const expenseStore = useExpenseStore();

  useEffect(() => {
    if (userDetails?.BusinessID) {
      expenseStore.loadCategoriesByBusiness(userDetails.BusinessID);
      const list = useExpenseStore.getState().getCategories(userDetails.BusinessID);
      setCategories(list);
    }
  }, [userDetails]);

  const fetchCategories = async (businessID) => {
    setCategories(useExpenseStore.getState().getCategories(businessID));
  };

  // ✅ Helper to show toast
  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleAddCategory = async () => {
    if (!comment) return showToast('Please enter a category name.', 'warning');
    if (!userDetails?.BusinessID) return showToast('Business ID not found.', 'error');

    const trimmed = comment.trim();
    if (!trimmed) return showToast('Category name cannot be empty.', 'warning');

    const isDuplicate = categories.some((cat) => cat === trimmed);
    if (isDuplicate) return showToast('This category already exists.', 'warning');

    try {
      await expenseStore.addCategory({
        CategoryName: comment,
        BusinessID: userDetails.BusinessID,
      });

      setComment('');
      fetchCategories(userDetails.BusinessID);

      showToast('Category added successfully!', 'success'); // ✅ Toast
    } catch (error) {
      console.error('Error adding category:', error);
      showToast('Failed to add category.', 'error'); // ✅ Toast
    }
  };

  const handleDeleteCategory = async (category) => {
    if (category.ExpensesCount > 0) {
      setOpenAlert(true);
      return;
    }

    try {
      await expenseStore.deleteCategory({
        CategoryID: category.ID,
        BusinessID: userDetails.BusinessID,
      });

      setCategories((prev) => prev.filter((cat) => cat.ID !== category.ID));
      showToast('Category deleted successfully!', 'success'); // ✅ Toast
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Error deleting category.', 'error'); // ✅ Toast
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          pb: 2,
          pt: 1,
          backgroundColor: 'white',
        }}
      >
        <PageHeader data="Manage Categories" />

        <Box sx={{ display: 'flex', mt: 1 }}>
          <TextField
            fullWidth
            label="Enter Category Name"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />

          <Button
            onClick={handleAddCategory}
            variant="contained"
            className="bg-primary"
            sx={{
              ml: 2,
              textTransform: 'none',
              fontSize: { xs: '0.85rem', sm: '0.99rem' },
            }}
          >
            Add
          </Button>
        </Box>

        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 1,
            px: 1,
            py: 1,
            bgcolor: '#f0f0f0',
            borderRadius: 1,
          }}
        >
          Existing Categories
        </Typography>
      </Box>

      {categories.map((category) => (
        <Box
          key={category.ID}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 1,
            border: '1px solid #ccc',
            borderRadius: 2,
          }}
        >
          <Typography>{category.Category}</Typography>

          <IconButton
            color="error"
            onClick={() => handleDeleteCategory(category)}
          >
            <Delete />
          </IconButton>
        </Box>
      ))}

      {/* Dialog Alert for Deletion Block */}
      <Dialog open={openAlert} onClose={() => setOpenAlert(false)}>
        <DialogTitle>Cannot Delete</DialogTitle>
        <DialogContent>
          This category has associated expenses and cannot be deleted.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlert(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar Toast (Top Right) */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // ✅ Top Right
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
